
import {
  Activity, AlertTriangle, BarChart3, CalendarCheck2, CheckCircle2, ClipboardList,
  Dumbbell, Eye, Film, Gauge, Search, ShieldAlert, Sparkles, Target, Users, WifiOff,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useData } from './DataContext';
import { getSupabase } from './supabase';
import { canCoach, isPlayerRole, isStaffRole } from './staffAccess';
import { flushOperationsQueue, offlineQueueCount, resilientWrite } from './operationsOffline';
import type { ModuleId } from './AppShell';

type AvailabilityStatus = 'available'|'maybe'|'unavailable'|'limited'|'injured'|'suspended'|'away';
type AvailabilityRow = { id?:string; player_id:string; fixture_key:string; status:AvailabilityStatus; reason?:string|null; updated_at?:string|null };
type ReadinessRow = { id?:string; player_id:string; entry_date:string; freshness:number; soreness:number; sleep:number; availability:string; note?:string|null };
type TrainingRow = { id?:string; player_id:string; session_date:string; attended:boolean; rpe:number; duration:number };
type ScoutRow = { id?:string; opponent:string; formation?:string|null; strengths?:string|null; weaknesses?:string|null; key_players?:string|null; set_pieces?:string|null; pressing_style?:string|null; notes?:string|null };
type AuditRow = { id?:string; action:string; entity_type:string; detail?:any; created_at?:string|null };
type VideoRow = { id?:string; match_id:string; minute:number; player_id?:string|null; label:string; url:string };

const today = () => new Date().toISOString().slice(0,10);
const fixtureKey = (date:string, opponent:string) => `${date}|${opponent}`.toLowerCase().replace(/\s+/g,'-');

function clamp(n:number,min:number,max:number){ return Math.max(min,Math.min(max,n)); }
function avg(nums:number[]){ return nums.length ? nums.reduce((a,b)=>a+b,0)/nums.length : 0; }

export function OperationsCentre({ onNavigate }: { onNavigate:(m:ModuleId)=>void }) {
  const { team, players, matches, lineups } = useData();
  const playerMode = isPlayerRole(team?.role);
  const staffMode = isStaffRole(team?.role);
  const coachMode = canCoach(team?.role);
  const [tab,setTab] = useState<'command'|'availability'|'readiness'|'load'|'trends'|'report'|'scouting'|'audit'>('command');
  const [availability,setAvailability] = useState<AvailabilityRow[]>([]);
  const [readiness,setReadiness] = useState<ReadinessRow[]>([]);
  const [training,setTraining] = useState<TrainingRow[]>([]);
  const [scouting,setScouting] = useState<ScoutRow[]>([]);
  const [audit,setAudit] = useState<AuditRow[]>([]);
  const [videos,setVideos] = useState<VideoRow[]>([]);
  const [selectedPlayerId,setSelectedPlayerId] = useState('');
  const [message,setMessage] = useState('');
  const [queue,setQueue] = useState(offlineQueueCount());
  const [readinessDraft,setReadinessDraft] = useState({freshness:4,soreness:2,sleep:4,availability:'available',note:''});
  const [scoutDraft,setScoutDraft] = useState({opponent:'',formation:'',strengths:'',weaknesses:'',key_players:'',set_pieces:'',pressing_style:'',notes:''});
  const [videoDraft,setVideoDraft] = useState({match_id:'',minute:0,player_id:'',label:'',url:''});

  const nextMatch = useMemo(() => [...matches]
    .filter(m=>new Date(m.match_date).getTime() >= Date.now()-86400000)
    .sort((a,b)=>a.match_date.localeCompare(b.match_date))[0] || null,[matches]);
  const currentFixtureKey = nextMatch ? fixtureKey(nextMatch.match_date, nextMatch.opponent || 'TBA') : `next|tba`;
  const completed = useMemo(()=>[...matches].filter(m=>m.status==='full_time'||m.goals_for!==null).sort((a,b)=>b.match_date.localeCompare(a.match_date)),[matches]);
  const selectedMatch = completed[0] || null;
  const selectedPlayer = players.find(p=>p.id===(playerMode && team?.player_id ? team.player_id : selectedPlayerId)) || players.find(p=>p.id===selectedPlayerId) || players[0] || null;

  useEffect(()=>{ if(!selectedPlayerId && players[0]) setSelectedPlayerId(players[0].id); },[players,selectedPlayerId]);

  async function load() {
    if(!team) return;
    const client=getSupabase();
    const safe = async(table:string, order?:string) => {
      try {
        let q:any=client.from(table).select('*').eq('team_id',team.id);
        if(order) q=q.order(order,{ascending:false});
        const {data,error}=await q;
        if(error) return [];
        return data || [];
      } catch { return []; }
    };
    const [a,r,t,s,au,v] = await Promise.all([
      safe('player_availability','updated_at'),
      safe('player_readiness','entry_date'),
      safe('training_attendance','session_date'),
      safe('opponent_scouting','updated_at'),
      safe('operations_audit_log','created_at'),
      safe('match_video_links','minute'),
    ]);
    setAvailability(a as AvailabilityRow[]);
    setReadiness(r as ReadinessRow[]);
    setTraining(t as TrainingRow[]);
    setScouting(s as ScoutRow[]);
    setAudit(au as AuditRow[]);
    setVideos(v as VideoRow[]);
    setQueue(offlineQueueCount());
  }
  useEffect(()=>{ void load(); },[team?.id]);
  useEffect(()=>{
    const handler=async()=>{ await flushOperationsQueue(); await load(); };
    window.addEventListener('online',handler);
    return()=>window.removeEventListener('online',handler);
  },[team?.id]);

  const relevantAvailability = availability.filter(a=>a.fixture_key===currentFixtureKey);
  const statusByPlayer = new Map(relevantAvailability.map(a=>[a.player_id,a]));
  const availableCount = players.filter(p=>['available','limited'].includes(statusByPlayer.get(p.id)?.status || '')).length;
  const maybeCount = players.filter(p=>statusByPlayer.get(p.id)?.status==='maybe').length;
  const unavailableCount = players.filter(p=>['unavailable','injured','suspended','away'].includes(statusByPlayer.get(p.id)?.status || '')).length;
  const missingCount = Math.max(0,players.length-relevantAvailability.length);

  const todayReadiness = readiness.filter(r=>r.entry_date===today());
  const readinessByPlayer = new Map(todayReadiness.map(r=>[r.player_id,r]));
  const readinessScore = (r:ReadinessRow) => Math.round(clamp((r.freshness*20)*.38 + ((6-r.soreness)*20)*.28 + (r.sleep*20)*.34,0,100));
  const squadReadiness = Math.round(avg(todayReadiness.map(readinessScore)));

  const latestLineup = lineups[0];
  const lineupState = latestLineup?.publication_status || (latestLineup ? 'draft' : 'missing');
  const reviewComplete = selectedMatch ? Boolean(selectedMatch.final_notes && selectedMatch.mvp_player_id) : false;

  async function saveAvailability(playerId:string,status:AvailabilityStatus,reason='') {
    if(!team) return;
    const payload={team_id:team.id,player_id:playerId,fixture_key:currentFixtureKey,status,reason:reason||null,updated_at:new Date().toISOString()};
    await resilientWrite('player_availability','upsert',payload);
    setAvailability(prev=>[...prev.filter(x=>!(x.player_id===playerId&&x.fixture_key===currentFixtureKey)),payload]);
    setQueue(offlineQueueCount()); setMessage(navigator.onLine?'Availability saved.':'Saved offline — will sync automatically.');
  }

  async function saveReadiness() {
    if(!team || !selectedPlayer) return;
    const payload={team_id:team.id,player_id:selectedPlayer.id,entry_date:today(),...readinessDraft,created_at:new Date().toISOString()};
    await resilientWrite('player_readiness','upsert',payload);
    setReadiness(prev=>[...prev.filter(x=>!(x.player_id===selectedPlayer.id&&x.entry_date===today())),payload]);
    setQueue(offlineQueueCount()); setMessage('Readiness saved.');
  }

  async function saveTraining(playerId:string, attended:boolean, rpe:number, duration:number) {
    if(!team) return;
    const payload={team_id:team.id,player_id:playerId,session_date:today(),attended,rpe,duration,updated_at:new Date().toISOString()};
    await resilientWrite('training_attendance','upsert',payload);
    setTraining(prev=>[...prev.filter(x=>!(x.player_id===playerId&&x.session_date===today())),payload]);
  }

  async function saveScout() {
    if(!team || !scoutDraft.opponent.trim()) return;
    const payload={team_id:team.id,...scoutDraft,updated_at:new Date().toISOString()};
    await resilientWrite('opponent_scouting','upsert',payload);
    setMessage('Opponent briefing saved.'); await load();
  }

  async function saveVideo() {
    if(!team || !videoDraft.match_id || !videoDraft.url.trim()) return;
    const payload={team_id:team.id,...videoDraft,player_id:videoDraft.player_id||null,created_at:new Date().toISOString()};
    await resilientWrite('match_video_links','insert',payload);
    setVideoDraft({...videoDraft,label:'',url:'',minute:0}); setMessage('Video link saved.'); await load();
  }

  const trendRows = useMemo(()=>{
    if(!selectedPlayer) return [];
    return completed.slice(0,8).map(m=>{
      const c=(m.contributions||[]).find(x=>x.player_id===selectedPlayer.id);
      return {date:m.match_date,opponent:m.opponent||'Opponent',minutes:Number(c?.minutes||0),goals:Number(c?.goals||0),assists:Number(c?.assists||0),rating:Number(c?.rating||0)};
    }).reverse();
  },[completed,selectedPlayer]);

  const publicReport = useMemo(()=>{
    if(!selectedMatch) return '';
    const motm=players.find(p=>p.id===selectedMatch.mvp_player_id);
    const top=[...(selectedMatch.contributions||[])].sort((a,b)=>Number(b.rating||0)-Number(a.rating||0)).slice(0,3)
      .map(c=>`${players.find(p=>p.id===c.player_id)?.name||'Player'} ${Number(c.rating||0).toFixed(1)}`).join(' · ');
    return `${new Date(selectedMatch.match_date).toLocaleDateString()} — Polonia Glasgow ${selectedMatch.goals_for ?? 0}:${selectedMatch.goals_against ?? 0} ${selectedMatch.opponent||''}\nMOTM: ${motm?.name||'TBA'}\nTop performers: ${top||'TBA'}\n${selectedMatch.final_notes||''}`.trim();
  },[selectedMatch,players]);

  const coachAlerts = [
    missingCount ? `${missingCount} availability responses missing` : '',
    todayReadiness.length < players.length ? `${players.length-todayReadiness.length} readiness check-ins missing` : '',
    lineupState !== 'published' ? `Lineup ${lineupState}` : '',
    selectedMatch && !reviewComplete ? 'Last match review incomplete' : '',
    queue ? `${queue} offline changes waiting to sync` : '',
  ].filter(Boolean);

  const playerOwnId = team?.player_id || '';
  const visiblePlayers = playerMode && playerOwnId ? players.filter(p=>p.id===playerOwnId) : players;
  const developmentSuggestions = useMemo(() => {
    return players.map(player => {
      const merged = { ...(player.attributes || {}), ...(player.mindset || {}) } as Record<string, number>;
      const lowest = Object.entries(merged)
        .filter(([,v])=>Number.isFinite(Number(v)))
        .sort((a,b)=>Number(a[1])-Number(b[1]))
        .slice(0,2);
      const labels = lowest.map(([k])=>k.replace(/_/g,' '));
      const focus = labels[0] || (player.position==='GK' ? 'handling / positioning' : 'first touch / decision making');
      const drill = player.position==='GK'
        ? `GK circuit: ${focus} → handling under pressure → 1v1 reaction → distribution`
        : `Position-specific circuit: ${focus} → constrained rondo → opposed repetition → small-sided game`;
      return {player, focus:labels.join(' + ') || focus, drill};
    }).slice(0,8);
  }, [players]);


  return <div className="operations-page">
    <section className="metal-card operations-hero">
      <div><p className="eyebrow">FOOTBALL OPERATIONS SYSTEM · V24</p><h1>{playerMode?'My Team Hub':'Coach Command Centre'}</h1>
      <p>{playerMode?'Availability, readiness and published team information.':'One operational layer across squad, matchday, performance and training.'}</p></div>
      <div className={`operations-network ${navigator.onLine?'online':'offline'}`}>{navigator.onLine?<CheckCircle2 size={18}/>:<WifiOff size={18}/>} {navigator.onLine?'Cloud ready':`Offline · ${queue} queued`}</div>
    </section>

    <nav className="operations-tabs">
      {[
        ['command','Command'],['availability','Availability'],['readiness','Readiness'],
        ...(!playerMode?[['load','Training Load']]:[]),
        ...(coachMode?[['trends','Trends'],['report','Post-Match'],['scouting','Scouting'],['audit','Audit']]:[])
      ].map(([id,label])=><button key={id} className={tab===id?'active':''} onClick={()=>setTab(id as any)}>{label}</button>)}
    </nav>
    {message && <div className="operations-toast">{message}</div>}

    {tab==='command' && <div className="operations-command-grid">
      <section className="metal-card ops-kpi"><span>AVAILABLE</span><strong>{availableCount}/{players.length}</strong><small>{maybeCount} maybe · {unavailableCount} out · {missingCount} waiting</small></section>
      <section className="metal-card ops-kpi"><span>READINESS</span><strong>{squadReadiness || '—'}</strong><small>{todayReadiness.length}/{players.length} check-ins today</small></section>
      <section className="metal-card ops-kpi"><span>LINEUP</span><strong>{String(lineupState).toUpperCase()}</strong><small>{nextMatch?.opponent||'No next fixture'}</small></section>
      <section className="metal-card ops-kpi"><span>LAST REVIEW</span><strong>{reviewComplete?'READY':'OPEN'}</strong><small>{selectedMatch?.opponent||'No completed match'}</small></section>
      {!playerMode && <section className="metal-card operations-attention">
        <div className="section-title-line"><div><p className="eyebrow">WHAT NEEDS ATTENTION?</p><h2>Today</h2></div><ShieldAlert size={24}/></div>
        {coachAlerts.length?coachAlerts.map((a,i)=><button key={i} onClick={()=>i===0?setTab('availability'):undefined}><AlertTriangle size={16}/>{a}</button>):<p className="ops-all-clear"><CheckCircle2 size={18}/> No critical workflow gaps detected.</p>}
      </section>}
      <section className="metal-card operations-next">
        <p className="eyebrow">NEXT MATCH</p><h2>{nextMatch?.opponent||'TBA'}</h2>
        <p>{nextMatch ? new Date(nextMatch.match_date).toLocaleString() : 'No fixture loaded'}</p>
        {!playerMode && <div className="ops-actions"><button onClick={()=>onNavigate('lineup')}>Lineup</button><button onClick={()=>onNavigate('matchday')}>Matchday</button><button onClick={()=>onNavigate('training')}>Training</button></div>}
      </section>
    </div>}

    {tab==='availability' && <section className="metal-card">
      <div className="section-title-line"><div><p className="eyebrow">RSVP / AVAILABILITY</p><h2>{nextMatch?.opponent||'Next fixture'}</h2></div><Users size={24}/></div>
      <div className="ops-player-list">
      {visiblePlayers.map(p=>{
        const current=statusByPlayer.get(p.id)?.status;
        return <article key={p.id} className="ops-player-row"><div><strong>{p.name}</strong><small>#{p.squad_number??'—'} · {p.position}</small></div>
          <div className="availability-buttons">
            {(['available','maybe','limited','injured','suspended','away','unavailable'] as AvailabilityStatus[]).map(s=><button key={s} className={current===s?`active ${s}`:s} onClick={()=>void saveAvailability(p.id,s)}>{s==='available'?'IN':s==='unavailable'?'OUT':s==='suspended'?'SUSP':s.toUpperCase()}</button>)}
          </div></article>
      })}
      </div>
    </section>}

    {tab==='readiness' && <section className="metal-card">
      <div className="section-title-line"><div><p className="eyebrow">10-SECOND CHECK-IN</p><h2>Daily Readiness</h2></div><Gauge size={24}/></div>
      {!playerMode && <select value={selectedPlayer?.id||''} onChange={e=>setSelectedPlayerId(e.target.value)}>{players.map(p=><option value={p.id} key={p.id}>{p.name}</option>)}</select>}
      <div className="readiness-input-grid">
        {([['freshness','Freshness'],['soreness','Soreness'],['sleep','Sleep']] as const).map(([key,label])=><label key={key}><span>{label}: <b>{(readinessDraft as any)[key]}/5</b></span><input type="range" min="1" max="5" value={(readinessDraft as any)[key]} onChange={e=>setReadinessDraft({...readinessDraft,[key]:Number(e.target.value)})}/></label>)}
      </div>
      <label className="ops-field"><span>Availability</span><select value={readinessDraft.availability} onChange={e=>setReadinessDraft({...readinessDraft,availability:e.target.value})}><option value="available">Fully available</option><option value="limited">Limited minutes</option><option value="unavailable">Unavailable</option></select></label>
      <label className="ops-field"><span>Note (optional)</span><input value={readinessDraft.note} onChange={e=>setReadinessDraft({...readinessDraft,note:e.target.value})}/></label>
      <button className="gold-button" onClick={()=>void saveReadiness()}>Save readiness</button>
      {!playerMode && <div className="readiness-board">{players.map(p=>{const r=readinessByPlayer.get(p.id);const score=r?readinessScore(r):0;return <div key={p.id}><span>{p.name}</span><b className={score>=75?'good':score>=55?'warn':'bad'}>{r?score:'—'}</b></div>})}</div>}
    </section>}

    {tab==='load' && !playerMode && <section className="metal-card">
      <div className="section-title-line"><div><p className="eyebrow">TRAINING ATTENDANCE + RPE</p><h2>Session Load</h2></div><Dumbbell size={24}/></div>
      <p className="muted">Simple load = session minutes × RPE. This is a workload trend, not an injury-risk diagnosis.</p>
      <div className="ops-player-list">{players.map(p=>{const row=training.find(t=>t.player_id===p.id&&t.session_date===today());const load=(row?.duration||0)*(row?.rpe||0);return <article className="ops-player-row load-row" key={p.id}><strong>{p.name}</strong>
        <label><input type="checkbox" checked={Boolean(row?.attended)} onChange={e=>void saveTraining(p.id,e.target.checked,row?.rpe||5,row?.duration||75)}/> Attended</label>
        <input type="number" min="1" max="10" value={row?.rpe||5} onChange={e=>void saveTraining(p.id,row?.attended??true,Number(e.target.value),row?.duration||75)}/>
        <input type="number" min="0" max="180" value={row?.duration||75} onChange={e=>void saveTraining(p.id,row?.attended??true,row?.rpe||5,Number(e.target.value))}/>
        <b>{load}</b></article>})}</div>
      <div className="training-suggestion-board">
        <p className="eyebrow">TRAINING BUILDER 2.0 · AUTO FOCUS</p>
        <h3>Weakness → drill → session</h3>
        {developmentSuggestions.map(({player,focus,drill})=><article key={player.id}><strong>{player.name}</strong><span>{focus}</span><p>{drill}</p></article>)}
      </div>
      <button className="steel-button" onClick={()=>onNavigate('training')}>Open Training Builder</button>
    </section>}

    {tab==='trends' && coachMode && <section className="metal-card">
      <div className="section-title-line"><div><p className="eyebrow">SEASON TRENDS</p><h2>Player trajectory</h2></div><BarChart3 size={24}/></div>
      <select value={selectedPlayer?.id||''} onChange={e=>setSelectedPlayerId(e.target.value)}>{players.map(p=><option value={p.id} key={p.id}>{p.name}</option>)}</select>
      <div className="trend-grid">{trendRows.map(r=><div className="trend-card" key={`${r.date}-${r.opponent}`}><small>{new Date(r.date).toLocaleDateString()}</small><strong>{r.rating?r.rating.toFixed(1):'—'}</strong><span>{r.opponent}</span><p>{r.minutes}' · {r.goals}G · {r.assists}A</p></div>)}</div>
      <div className="trend-summary"><span>Last 5 avg rating <b>{avg(trendRows.slice(-5).filter(x=>x.rating).map(x=>x.rating)).toFixed(1)}</b></span><span>Minutes <b>{trendRows.reduce((s,x)=>s+x.minutes,0)}</b></span><span>G+A <b>{trendRows.reduce((s,x)=>s+x.goals+x.assists,0)}</b></span></div>
    </section>}

    {tab==='report' && coachMode && <section className="metal-card">
      <div className="section-title-line"><div><p className="eyebrow">AUTOMATED POST-MATCH INTELLIGENCE</p><h2>{selectedMatch?.opponent||'No completed match'}</h2></div><Sparkles size={24}/></div>
      {selectedMatch ? <>
        <pre className="auto-report">{publicReport}</pre>
        <div className="ops-actions"><button onClick={()=>navigator.clipboard?.writeText(publicReport)}>Copy public report</button><button onClick={()=>onNavigate('analysis')}>Open Coach Review</button></div>
        <hr/>
        <h3><Film size={18}/> Event / video links</h3>
        <div className="video-link-form">
          <select value={videoDraft.match_id} onChange={e=>setVideoDraft({...videoDraft,match_id:e.target.value})}><option value="">Select match</option>{completed.map(m=><option key={m.id} value={m.id}>{m.match_date} · {m.opponent}</option>)}</select>
          <input type="number" placeholder="Minute" value={videoDraft.minute} onChange={e=>setVideoDraft({...videoDraft,minute:Number(e.target.value)})}/>
          <select value={videoDraft.player_id} onChange={e=>setVideoDraft({...videoDraft,player_id:e.target.value})}><option value="">Team / no player</option>{players.map(p=><option value={p.id} key={p.id}>{p.name}</option>)}</select>
          <input placeholder="Label" value={videoDraft.label} onChange={e=>setVideoDraft({...videoDraft,label:e.target.value})}/>
          <input placeholder="Hudl / Veo / video URL" value={videoDraft.url} onChange={e=>setVideoDraft({...videoDraft,url:e.target.value})}/>
          <button onClick={()=>void saveVideo()}>Save link</button>
        </div>
        <div className="video-links">{videos.filter(v=>v.match_id===selectedMatch.id).map(v=><a key={v.id||`${v.minute}${v.url}`} href={v.url} target="_blank" rel="noreferrer">{v.minute}' · {v.label||'Video'}</a>)}</div>
      </>:<p>No completed match data.</p>}
    </section>}

    {tab==='scouting' && coachMode && <section className="metal-card">
      <div className="section-title-line"><div><p className="eyebrow">OPPONENT INTELLIGENCE</p><h2>Scouting Brief</h2></div><Target size={24}/></div>
      <div className="scout-form">
        <input placeholder="Opponent" value={scoutDraft.opponent} onChange={e=>setScoutDraft({...scoutDraft,opponent:e.target.value})}/>
        <input placeholder="Preferred formation" value={scoutDraft.formation} onChange={e=>setScoutDraft({...scoutDraft,formation:e.target.value})}/>
        <textarea placeholder="Strengths" value={scoutDraft.strengths} onChange={e=>setScoutDraft({...scoutDraft,strengths:e.target.value})}/>
        <textarea placeholder="Weaknesses" value={scoutDraft.weaknesses} onChange={e=>setScoutDraft({...scoutDraft,weaknesses:e.target.value})}/>
        <textarea placeholder="Key players" value={scoutDraft.key_players} onChange={e=>setScoutDraft({...scoutDraft,key_players:e.target.value})}/>
        <textarea placeholder="Set pieces" value={scoutDraft.set_pieces} onChange={e=>setScoutDraft({...scoutDraft,set_pieces:e.target.value})}/>
        <textarea placeholder="Pressing style" value={scoutDraft.pressing_style} onChange={e=>setScoutDraft({...scoutDraft,pressing_style:e.target.value})}/>
        <textarea placeholder="Coach notes" value={scoutDraft.notes} onChange={e=>setScoutDraft({...scoutDraft,notes:e.target.value})}/>
        <button className="gold-button" onClick={()=>void saveScout()}>Save opponent brief</button>
      </div>
      <div className="scout-list">{scouting.map(s=><article key={s.id||s.opponent}><strong>{s.opponent}</strong><span>{s.formation||'Formation TBA'}</span><p><b>Strengths:</b> {s.strengths||'—'}</p><p><b>Weaknesses:</b> {s.weaknesses||'—'}</p></article>)}</div>
    </section>}

    {tab==='audit' && coachMode && <section className="metal-card">
      <div className="section-title-line"><div><p className="eyebrow">DATA GOVERNANCE</p><h2>Audit Log</h2></div><ClipboardList size={24}/></div>
      <p className="muted">V24 records operations changes. Core legacy modules can be progressively wired into the same log without changing their UI.</p>
      <div className="audit-list">{audit.length?audit.slice(0,80).map((a,i)=><div key={a.id||i}><span>{a.created_at?new Date(a.created_at).toLocaleString():'—'}</span><strong>{a.action}</strong><small>{a.entity_type}</small></div>):<p>No V24 audit events yet.</p>}</div>
    </section>}
  </div>;
}
